/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import {PagePermissionType} from "../models/page-permission-type";
import {Permission} from "../models/permission";
import {User} from '../models/user';

import {DialogWebSocketService} from "./dialog-web-socket.service";
import {PagePermissionService} from "./page-permission.service";
import { CompiledLicenseDTO } from '../models/compiled-license-dto';
import { Url_Base } from 'app/rest-constants';

@Injectable()
export class AuthenticationService {

  private headers = new HttpHeaders({
    'Content-Type': 'application/json'
  });

  private mCurrentUserInfo: UserInfo = null;
  private mTokenExpirationDate: Date = null;
  private mCurrentLicenseInfo: CompiledLicenseDTO;

  constructor(
    private http: HttpClient,
    private mDialogWebSocketService: DialogWebSocketService,
    private pagePermissionService: PagePermissionService,
  ) { }


  private getUrl(): string {
    return `${Url_Base}/multifactor/secure/`;
  }

  public async initialize(): Promise<void> {
    console.log("Initializing Authentication Service...");

    //Get the Cached User Info from local storage
    this.mCurrentUserInfo = JSON.parse(localStorage.getItem('currentUser')) as UserInfo;
    if (this.mCurrentUserInfo != null) {
      console.log("Validating Token on startup...");
      try {
        this.mTokenExpirationDate = await this.validateTokenGetExpirationDate();
      } catch (error) {
        console.error("Problem validating Authentication Token during initialization...", error);
        this.mCurrentUserInfo = null;
        this.updateLocalStorage();
      }
      console.warn("Valid Token! Exp Date: " + this.mTokenExpirationDate);

    }

    //Refresh the Licensing
    await this.refreshLicense();

    //Refresh the Page Permissions based on the loaded in User Info
    this.refreshPagePermissions();

    //Refresh the Dialog Web Socket
    this.mDialogWebSocketService.refreshDialogWebSocket(this.getToken());

    //Restart the session timer
    //SessionExpirationDialogComponent.getSessionExpirationParameters();

    console.log("Authentication Service Initialized!  Current User: " + (this.mCurrentUserInfo == null? "*NULL*" : (this.mCurrentUserInfo.mUser.id + "/" + this.mCurrentUserInfo.mUser.login)));
  }

  public async login(userId: string, password: string): Promise<LoginResponse> {
    const url = this.getUrl() + "login";
    console.log("login() - User Id: " + userId + ", Password: " + password + "... URL: " + url);

    const retVal = new LoginResponse();
    retVal.isSuccess = false;

    let user: User = null;
    let token: string = null;
    try {
      const response = await this.http.post<any>(
        url,
        { login: userId, password: password },
        { headers: this.headers, observe: 'response' }
      ).toPromise();
      const user = response.body;
      retVal.isTwoFactorEnabled= response.body.twoFactorEnabled; 

      // -------------------------
      // CASE 1: 2FA REQUIRED (206)
      // -------------------------
      if (retVal.isTwoFactorEnabled) {
        console.log("2FA Required!");
        retVal.isSuccess = true;
        localStorage.setItem("multifactorUser", JSON.stringify(response.body));
        return retVal;
      }

      // -------------------------
      // CASE 2: NORMAL LOGIN (200)
      // -------------------------
      const token = response.headers.get('Authorization');
      
      console.log("Normal Login Successful");

      if (user != null && token != null) {
        //Return true to indicate a successful login
        retVal.isSuccess = true;
        await this.setCurrentUserInfo(user, token);
        this.setCookie('jwtToken', token, 2);

        // Save token expiration date
        this.mTokenExpirationDate = await this.validateTokenGetExpirationDate();
      }
    } catch (error) {
      // In a real world app, you might use a remote logging infrastructure
      let errMsg: string;
      if (error instanceof Response) {
        if (error.status === 504) {
          errMsg = error.statusText;
        } else if (error.status === 401) {
          errMsg = (await error.text()).toString();
        } else if (error.status === 0) {
          errMsg = "Problem communicating with Edgefinity Server.";
        } else {
          errMsg = `${error.status} - ${error.statusText || ''}`;
        }
      } else {
        errMsg = error.error ? error.error : error.toString();
      }
      console.error(errMsg);
      retVal.isSuccess = false;
      retVal.error = errMsg;
    }

    return retVal;
  }

  setCookie(name: string, value: string, expiryValue: number): void {
    const maxAge = expiryValue * 60 * 60; //In seconds
    document.cookie = `${name}=${value}; Path=/; SameSite=Lax`;
  }

  public getTokenExpirationDate(): Date {
    return this.mTokenExpirationDate;
  }

  private async setCurrentUserInfo(pUser: User, pToken: string): Promise<void> {//Set the Current User Info
    let userInfo: UserInfo = null;
    if (pUser != null && pToken != null) {
      userInfo = new UserInfo();
      userInfo.mUser = pUser;
      userInfo.mToken = pToken;
      userInfo.mPagePermissions = this.getPagePermissionsForUser(pUser);
    }

    this.mCurrentUserInfo = userInfo;
    this.updateLocalStorage();

    await this.refreshLicense();

    this.refreshPagePermissions();

    //Refresh the Dialog Web Socket... Open it using the Token for the User that just signed in
    this.mDialogWebSocketService.refreshDialogWebSocket(pToken);
  }

  public async logout(): Promise<void> {
    //Set the Current User Info to null to log the User out (Forget any information about the currently logged in User)
    await this.setCurrentUserInfo(null, null);

    //Use the setCookue function to set value of cookie to null & expiry to 0
    this.setCookie('jwtToken', "", 0);
  }

  public async reauthorizeUser(): Promise<void> {
    return await this._reauthorizeUser();
  }

  private async _reauthorizeUser(): Promise<void> {
    const url = this.getUrl() + "reauthorize";

    const response = await this.http
      .post<User>(url, null, { headers: new HttpHeaders({'Content-Type': 'application/json', 'Authorization': this.getToken()}), observe: 'response'})
      .toPromise();

    const user = response.body as User;
    const token = response.headers.get('Authorization');
    this.setCurrentUserInfo(user, token);
    this.setCookie('jwtToken', token, 2);

    this.mTokenExpirationDate = await this.validateTokenGetExpirationDate();
    return null;
  }

  public validateToken(): Promise<any> {
      const url = this.getUrl() + "validate";
      return this .http
                  .get(url, {headers: new HttpHeaders({'Authorization': this.getToken()})})
                  .toPromise();
  }

  public validateTokenGetExpirationDate(): Promise<Date> {
    const url = this.getUrl() + "expiration";
    return this .http
      .get<any>(url, {headers: new HttpHeaders({'Authorization': this.getToken()}), observe: 'response'})
      .toPromise()
      .then(response => {
          return new Date(+response.body);
        }
      );
  }


  public getToken(): string {
    return this.mCurrentUserInfo == null? null : this.mCurrentUserInfo.mToken;
  }

  public getUser(): User {
    return this.mCurrentUserInfo == null? null : this.mCurrentUserInfo.mUser;
  }

  public getPagePermissions(): {[propKey: string]: Permission} {
    return this.mCurrentUserInfo == null? null : this.mCurrentUserInfo.mPagePermissions;
  }

  public updateUser(user: User): void {
    //Update the login information's User, and recalculate the Page Permissions based on the new User information
    this.mCurrentUserInfo.mUser = user;
    this.mCurrentUserInfo.mPagePermissions = this.getPagePermissionsForUser(user);

    this.updateLocalStorage();

    //Refresh the cached page permissions since they might have changed
    this.pagePermissionService.refreshCachedPagePermissions(this.mCurrentUserInfo.mPagePermissions);
  }

  public isLoggedIn(): boolean {
    return this.mCurrentUserInfo != null;
  }

  private refreshPagePermissions(): void {
    this.pagePermissionService.refreshCachedPagePermissions(this.mCurrentUserInfo == null? null : this.mCurrentUserInfo.mPagePermissions);
  }

  private updateLocalStorage(): void {
    if (this.mCurrentUserInfo != null) {
      localStorage.setItem('currentUser', JSON.stringify(this.mCurrentUserInfo));
    } else {
      localStorage.removeItem('currentUser');
    }
  }

  private getPagePermissionsForUser(user: User): {[propKey: string]: Permission} {
    const map: {[propKey: string]: Permission} = {};

    if (user != null && user.groups != null) {

      //Cycle through the User's groups
      user.groups.forEach(group => {
        if (group.pagePermissions != null) {

          //Cycle through the Group's Page Permissions
          for (const key of Object.keys(group.pagePermissions)) {

            //Attempt to get the enum for the Page Permission Key
            const pagePermissionType = this.getPagePermissionType(key);

            //If a Page Permission Type was obtained for the Key, add/update the permissions for that page
            if (pagePermissionType != null) {
              const value: Permission = group.pagePermissions[key];

              if (map[pagePermissionType] != null) {
                //Seen this page mulitple times now, retrieve/update the Permission for the Page so that it is the most inclusive (two permissions with one set to true and one to false = true)
                const currentPermission = map[pagePermissionType];
                currentPermission.create = currentPermission.create || value.create;
                currentPermission.read = currentPermission.read || value.read;
                currentPermission.update = currentPermission.update || value.update;
                currentPermission.delete = currentPermission.delete || value.delete;
                map[pagePermissionType] = currentPermission;
              } else {
                //First time seeing the Page key, store the permission as the one for the Page
                map[pagePermissionType] = value;
              }
            }
          }
        }
      });
    }

    return map;
  }

  private getPagePermissionType(permissionKey: string): PagePermissionType {
    if (permissionKey != null) {
      const typeKey = Object.keys(PagePermissionType).find(key => PagePermissionType[key] == permissionKey);
      return typeKey == null? null: typeKey as PagePermissionType;
    } else {
      return null;
    }
  }




  public async refreshLicense(): Promise<void> {
    this.mCurrentLicenseInfo = null;
    const token = this.getToken();
    if (token != null) {
      this.mCurrentLicenseInfo = await this.getCompiledLicenseWithToken(token);
    }
  }

  public getCurrentLicenseInfo(): CompiledLicenseDTO {
    return this.mCurrentLicenseInfo;
  }

  public isCurrentLicenseInfoValid(): boolean {
    return true;
    //return this.mCurrentLicenseInfo == null? false : this.mCurrentLicenseInfo.is_valid;
  }

  private getCompiledLicenseWithToken(token: string): Promise<CompiledLicenseDTO> {
    const callUrl = `${Url_Base}/license/compiled-license`;
    return this.http
      .get(callUrl,
        {headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': token
          })
        }
      )
      .toPromise()
      .then(response => {
        console.log("Retrieved current License with Token '" + token + "': " + response);
        return response as CompiledLicenseDTO;
        }
      );
  }

  public verifyOtp(otp: string, userId: string, phone: string): Promise<any> {
    const url = this.getUrl() + "verify-multifactor-otp";

    return this.http.post<any>(
      url,
      { otp: otp, userId: userId, phone: phone },
      { headers: this.headers, observe: 'response' }
    ).toPromise()
      .then(response => {
        return {
          user: response.body,              // backend returns user in BODY
          jwtToken: response.headers.get("Authorization") // JWT in header
        };
      });
  }

  public resendOtp(phone: string): Promise<void> {
    const url = this.getUrl() + "resend-multifactor-otp";
    return this.http.post<void>(url, { phone: phone }, { headers: this.headers }).toPromise();
  }


}

class UserInfo {
  mUser: User;
  mToken: string;
  mPagePermissions: {[propKey: string]: Permission};
}

export class LoginResponse {
  isSuccess: boolean;
  isTwoFactorEnabled: boolean;
  error: string;
}
